import React, { FC, useLayoutEffect, useRef, useState } from "react";
import dayjs from "dayjs";

/**
 * Stage 3 — the Overall track becomes a trend (handoff §03, §10 TREND).
 *
 * A fixed 0–100 y-domain with the 40 and 70 band boundaries as dashed
 * gridlines, so the same reading survives the transition from the track: level
 * is still vertical position against the same two thresholds. Strictly linear —
 * y = bottom − 1.17 × value — never evenly-spaced gridlines, or the banded
 * region below 40 would draw the same height as 40–70 and every slope would lie.
 *
 * ONE DEVIATION from the handoff's `viewBox 640×150`: the width is MEASURED,
 * exactly as SecondsChart does, because a fixed viewBox scales the axis text with
 * the drawing — 9.5px labels would render at ~15px on a wide panel and under
 * 3px at the 193px nested host. Every geometric value from the handoff is kept
 * (y-mapping, gridlines, radii, strokes); only the x-scale is real pixels.
 *
 * Every colour arrives through `style` — SVG presentation attributes cannot
 * resolve `var()`.
 */

export interface TrendPoint {
  date: string;
  value: number;
}

const HEIGHT = 150;
// Plot top is 6 — implied by PLOT_BOTTOM − 1.17 × 100.
const PLOT_BOTTOM = 123;
const LEFT = 60;
const RIGHT_PAD = 20;
const Y_UNIT = 1.17;

interface Props {
  points: TrendPoint[];
  /** Point label under the axis; defaults to the month ("MMM"). */
  formatLabel?: (iso: string) => string;
  /** Screen-reader name; defaults to the Overall-by-month wording. */
  ariaLabel?: string;
}

const monthLabel = (iso: string) => dayjs(iso).format("MMM");

export const ScoreTrend: FC<Props> = ({ points, formatLabel = monthLabel, ariaLabel }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const apply = () => setWidth(Math.max(160, el.clientWidth));
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (points.length < 2) return null;

  const right = Math.max(LEFT + 40, width - RIGHT_PAD);
  const span = Math.max(1, points.length - 1);
  const x = (i: number) => LEFT + ((right - LEFT) * i) / span;
  const y = (v: number) => PLOT_BOTTOM - Y_UNIT * Math.max(0, Math.min(100, v));

  const coords = points.map((p, i) => ({ ...p, x: x(i), y: y(p.value) }));
  const line = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const wash = `M${line.split(" ").join(" L")} L${coords[coords.length - 1].x.toFixed(1)},${PLOT_BOTTOM} L${coords[0].x.toFixed(1)},${PLOT_BOTTOM} Z`;
  const latest = coords[coords.length - 1];

  // Five month labels at most; first and last only under 320px.
  const narrow = width < 320;
  const labelEvery = narrow ? span : Math.max(1, Math.ceil(span / 4));
  const showLabel = (i: number) => i === 0 || i === span || (!narrow && i % labelEvery === 0);
  const anchorFor = (i: number): "start" | "middle" | "end" => {
    if (i === 0) return "start";
    if (i === span) return "end";
    return "middle";
  };

  const grid = [
    { v: 0, dashed: false },
    { v: 40, dashed: true },
    { v: 70, dashed: true },
    { v: 100, dashed: false },
  ];

  return (
    <div ref={hostRef} className="w-full">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
        height={HEIGHT}
        style={{ display: "block", maxWidth: "100%", overflow: "visible" }}
        role="img"
        aria-label={ariaLabel ?? `Overall score by month, ${points.length} scores, now ${latest.value}`}
      >
        {grid.map((g) => (
          <line
            key={g.v}
            x1={coords[0].x}
            x2={right}
            y1={y(g.v)}
            y2={y(g.v)}
            style={{
              stroke: g.dashed ? "var(--track)" : "var(--border)",
              strokeWidth: 1,
              strokeDasharray: g.dashed ? "3 4" : undefined,
            }}
          />
        ))}
        {grid.map((g) => (
          <text
            key={`l-${g.v}`}
            x={LEFT - 10}
            y={y(g.v) + 3.5}
            textAnchor="end"
            style={{ fill: "var(--text-2)", fontSize: 9.5, fontVariantNumeric: "tabular-nums" }}
          >
            {g.v}
          </text>
        ))}

        <path d={wash} style={{ fill: "var(--tint)" }} />
        <polyline
          points={line}
          style={{ fill: "none", stroke: "var(--accent)", strokeWidth: 2, strokeLinejoin: "round", strokeLinecap: "round" }}
        />
        {coords.slice(0, -1).map((c) => (
          <circle key={c.date} cx={c.x} cy={c.y} r={3} style={{ fill: "var(--surface)", stroke: "var(--accent)", strokeWidth: 2 }} />
        ))}
        {/* The latest point takes the band colour from the wrapper via currentColor. */}
        <circle cx={latest.x} cy={latest.y} r={4.5} style={{ fill: "var(--surface)", stroke: "currentColor", strokeWidth: 2.5 }} />

        {coords.map((c, i) =>
          showLabel(i) ? (
            <text
              key={`m-${c.date}`}
              x={c.x}
              y={PLOT_BOTTOM + 18}
              textAnchor={anchorFor(i)}
              className={i === span ? "font-CircularStdBlack" : undefined}
              style={{ fill: i === span ? "var(--text)" : "var(--text-2)", fontSize: 10 }}
            >
              {formatLabel(c.date)}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
};

export default ScoreTrend;
